import { useMemo } from 'react';
import {
  useIncidentDetailContext,
  useUserLanguage,
  useAddIncidentExpenseFormModalContext,
} from '@/customHooks';
import { advancedNormalizeDate, formatCurrency } from '@/helpers';
import { Button } from '../atoms';
import textsData from '@/textData/IncidentDetailTexts.json';
import { AddIncidentExpenseFormModal } from '../templates';

export const IncidentDetailExpensesList = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.expensesList : textsData.en.expensesList;
  const title = userLanguage === 'es' ? 'Gastos del Incidente' : 'Incident Expenses';
  const emptyMsg = userLanguage === 'es'
    ? 'No hay gastos registrados para este incidente'
    : 'No expenses found for this incident';
  const invoiceHeader = userLanguage === 'es' ? 'Número de Factura' : 'Invoice Number';

  const { incident } = useIncidentDetailContext();
  const { openModal } = useAddIncidentExpenseFormModalContext();

  const flattenedData = useMemo(
    () => {
      const incidentExpenses = incident?.incidentExpenses || [];

      return incidentExpenses.map((expense) => ({
        id: expense.id,
        invoiceNumber: expense.invoiceNumber || '-',
        description: expense.description || '-',
        date: expense.invoiceDate,
        amount: formatCurrency(expense.amount, userLanguage),
      }))
    },
    [incident, userLanguage]
  );

  const hasData = flattenedData.length > 0;

  return (
    <>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <h2 className="text-xl font-semibold">{title}</h2>
        <Button onClick={() => incident?.id && openModal(incident.id)}>
          + {texts.createButton}
        </Button>
      </div>

      {hasData ? (
        <div className="mt-4 w-full overflow-x-auto rounded-xl border border-[var(--color-border)] bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-300 bg-white">
                <th className="p-4 text-left font-bold">{texts.tableHeader.date}</th>
                <th className="p-4 text-left font-bold">{invoiceHeader}</th>
                <th className="p-4 text-left font-bold">{texts.tableHeader.description}</th>
                <th className="p-4 text-left font-bold">{texts.tableHeader.amount}</th>
              </tr>
            </thead>
            <tbody>
              {flattenedData.map((expense, index) => (
                <tr
                  key={expense.id}
                  className={`border-b border-[var(--color-border)] ${index % 2 !== 0 ? 'bg-background2' : 'bg-white'}`}
                >
                  <td className="p-4 align-middle">
                    {expense.date
                      ? (
                        <span title={String(expense.date)}>
                          {advancedNormalizeDate(userLanguage, new Date(expense.date).toISOString()) || '-'}
                        </span>
                      )
                      : '-'}
                  </td>
                  <td className="p-4 align-middle">{expense.invoiceNumber}</td>
                  <td className="p-4 align-middle">{expense.description}</td>
                  <td className="p-4 align-middle">{expense.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-8">
          <b>{emptyMsg}</b>
        </p>
      )}
      <AddIncidentExpenseFormModal />
    </>
  );
};
